import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Copy, Search, Sparkles } from "lucide-react";
import { DOCS_MARKDOWN } from "../lib/docsMarkdown";
import { Navigate } from "react-router-dom";
import { Logo } from "../components/Logo";
import { useAuth } from "../auth/AuthProvider";
import { PIXEL_GUIDES, type GuideBlock } from "../ads/pixelGuides";
import { pixelInlineScript } from "../ads/pixelSnippet";

// Private API reference for the Oxwell Ads API, styled after modern
// developer docs: grouped sidebar with search, a quickstart that launches a
// real campaign, per-resource endpoint indexes, an "On this page" rail, and
// collapsible parameter references for the heavy bodies.

type RefRow = [string, string, string];

const NAV: ReadonlyArray<readonly [string, ReadonlyArray<readonly [string, string]>]> = [
  ["Get started", [
    ["quickstart", "Quickstart"],
    ["concepts", "Core concepts"],
  ]],
  ["Campaigns", [
    ["campaigns", "Ad campaigns"],
    ["ad-groups", "Ad groups"],
    ["ads", "Ads"],
    ["files", "Files"],
    ["social", "Social accounts"],
  ]],
  ["Tracking", [
    ["pixel", "Pixel & websites"],
    ["capi", "Server-side events (CAPI)"],
    ["reporting", "Reporting"],
  ]],
  ["Resources", [
    ["conventions", "Conventions & errors"],
  ]],
];

// Endpoint rows per section, shared by the index tables, the "On this page"
// rail, and sidebar search.
const REF: Record<string, RefRow[]> = {
  campaigns: [
    ["GET", "/ad_campaigns", "List campaigns. first, after, direction."],
    ["GET", "/ad_campaigns/{id}", "Retrieve one, with delivery and attribution metrics."],
    ["POST", "/ad_campaigns", "Create a draft campaign."],
    ["PATCH", "/ad_campaigns/{id}", "Update fields; status: \"active\" launches a draft."],
    ["DELETE", "/ad_campaigns/{id}", "Delete a campaign."],
  ],
  "ad-groups": [
    ["GET", "/ad_groups", "List. Filter with ad_campaign_id."],
    ["GET", "/ad_groups/{id}", "Retrieve one."],
    ["POST", "/ad_groups", "Create inside a campaign."],
    ["PATCH", "/ad_groups/{id}", "Update budget, targeting, schedule."],
    ["GET", "/ad_groups/targeting_options", "Search targeting categories for detailed_targeting. Requires platform=meta and search."],
    ["POST", "/ad_groups/estimate_reach", "Estimate audience size for a targeting spec. The body requires platform: meta."],
    ["DELETE", "/ad_groups/{id}", "Delete an ad group."],
  ],
  ads: [
    ["GET", "/ads", "List. Filter with ad_group_id or ad_campaign_id."],
    ["GET", "/ads/{id}", "Retrieve one."],
    ["POST", "/ads", "Create an ad in an ad group."],
    ["PATCH", "/ads/{id}", "Update creative, copy, destination."],
    ["DELETE", "/ads/{id}", "Delete an ad."],
  ],
  files: [
    ["POST", "/files", "Reserve an upload: { filename, byte_size?, multipart? }. Returns the upload URL(s)."],
    ["POST", "/files/{id}/complete", "Finalize a multipart upload with the part ETags."],
    ["GET", "/files/{id}", "Check processing; use the file in an ad once ready."],
    ["GET", "/files", "List uploaded files."],
  ],
  social: [
    ["GET", "/social_accounts", "The Facebook Pages and Instagram profiles connected to this ad account (sacc_…)."],
  ],
  pixel: [
    ["POST", "/events/validate_pixel", "Check that the pixel is firing: { url? } reads a page for the snippet, or omit url to check from received events."],
    ["GET", "/events/pulse", "A live feed of the most recent events the pixel is sending."],
  ],
  capi: [
    ["POST", "/events", "Send a server-side conversion event with match keys and attribution context."],
    ["GET", "/events", "Read raw events: a person's journey or the account's recent activity."],
  ],
  reporting: [
    ["GET", "/stats", "List every metric with its unit and the properties it can break down by."],
    ["GET", "/stats/{metric}", "Time series for a metric. Ads: ad_spend, ad_delivery. Traffic metrics cover your website."],
    ["GET", "/ad_reports", "Daily delivery report rows, when you prefer report-shaped data."],
  ],
  conventions: [
    ["400", "Bad request", "Invalid or missing parameters; the message says which."],
    ["401", "Unauthorized", "Missing, malformed, or revoked API key."],
    ["404", "Not found", "Unknown object, another account's object, or an endpoint outside this API."],
    ["409", "Conflict", "The action conflicts with the object's state, e.g. editing a non-writable campaign."],
    ["429", "Rate limited", "Slow down and retry with backoff."],
  ],
};

// The Shopify custom-pixel script is shared with the in-app Shopify guide;
// the reference shows it with a placeholder account id.
const DOCS_ACCOUNT_ID = "biz_XXXXXXXXXXXX";
const SHOPIFY_PIXEL_SCRIPT = (PIXEL_GUIDES.find((guide) => guide.key === "shopify")?.steps
  .flatMap((step) => step.blocks)
  .find((block): block is Extract<GuideBlock, { type: "code" }> => block.type === "code")?.code ?? "")
  .replaceAll("{{ACCOUNT_ID}}", DOCS_ACCOUNT_ID);

const ON_PAGE_EXTRAS: Record<string, string[]> = {
  quickstart: ["1. Verify your key", "2. Create a campaign", "3. Create an ad group", "4. Upload a creative", "5. Create the ad & launch"],
  concepts: ["Scoped keys", "Headers", "Object model", "Lifecycle actions"],
  pixel: ["Install the snippet", "Install on a site builder", "Shopify", "WebinarJam", "Calendly & iClosed", "Standard events", "Custom events", "Where to fire events", "Sales on your checkout", "Event IDs", "Customer fields", "Verify the install"],
  capi: ["Send an event", "Match keys", "Deduplication", "Instant-form leads"],
};

function slugify(method: string, path: string): string {
  return `${method}-${path}`.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function CodeBlock({ code, lang = "bash" }: { code: string; lang?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    }).catch(() => undefined);
  };
  return <div className="docs-code">
    <div className="docs-code__bar">
      <span className="docs-code__lang">{lang}</span>
      <button type="button" onClick={copy} aria-label="Copy code">{copied ? <Check /> : <Copy />}{copied ? "Copied" : "Copy"}</button>
    </div>
    <pre>{code}</pre>
  </div>;
}

function EndpointIndex({ rows }: { rows: RefRow[] }) {
  return <table className="docs-index">
    <tbody>
      {rows.map(([method, path, description]) => <tr key={`${method}${path}`} id={slugify(method, path)}>
        <td><span className={`docs-method docs-method--${method.toLowerCase()}`}>{method}</span></td>
        <td><code>{path}</code></td>
        <td>{description}</td>
      </tr>)}
    </tbody>
  </table>;
}

function Fields({ label, rows }: { label: string; rows: RefRow[] }) {
  return <div className="docs-fields">
    <h4 className="docs-fields__title">{label}</h4>
    <div className="docs-params">
      {rows.map(([name, type, description]) => {
        const required = /required/i.test(type);
        const typeClean = type.replace(/,?\s*required/i, "").trim();
        return <div className="docs-param" key={name}>
          <div className="docs-param__head">
            <code>{name}</code>
            {typeClean && <span className="docs-param__type">{typeClean}</span>}
            {required && <span className="docs-param__req">required</span>}
          </div>
          <p>{description}</p>
        </div>;
      })}
    </div>
  </div>;
}

function Step({ number, title, children, code }: { number: number; title: string; children: React.ReactNode; code: string }) {
  return <div className="docs-step">
    <div className="docs-step__copy">
      <span className="docs-step__number">{number}</span>
      <div><strong>{title}</strong><p>{children}</p></div>
    </div>
    <CodeBlock code={code} />
  </div>;
}

export function ApiDocsPage() {
  const { session, booting } = useAuth();
  const [active, setActive] = useState<string>("quickstart");
  const [query, setQuery] = useState("");
  const [aiCopied, setAiCopied] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const copyForAi = () => {
    void navigator.clipboard.writeText(DOCS_MARKDOWN).then(() => {
      setAiCopied(true);
      window.setTimeout(() => setAiCopied(false), 1800);
    }).catch(() => undefined);
  };

  // Scroll spy: highlight the section currently in view in the side nav.
  useEffect(() => {
    if (!session) return;
    const headings = bodyRef.current?.querySelectorAll("section[id]");
    if (!headings || headings.length === 0) return;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) setActive(visible[0].target.id);
    }, { rootMargin: "-90px 0px -65% 0px" });
    headings.forEach((heading) => observer.observe(heading));
    return () => observer.disconnect();
  }, [session]);

  // Deep links from the dashboard (/api-docs#pixel) land after the session boots.
  useEffect(() => {
    if (!session || !window.location.hash) return;
    document.getElementById(window.location.hash.slice(1))?.scrollIntoView();
  }, [session]);

  const needle = query.trim().toLowerCase();
  const nav = useMemo(() => {
    if (!needle) return NAV;
    return NAV
      .map(([group, items]) => [group, items.filter(([id, label]) =>
        label.toLowerCase().includes(needle)
        || (REF[id] || []).some(([method, path]) => path.toLowerCase().includes(needle) || method.toLowerCase() === needle))] as const)
      .filter(([, items]) => items.length > 0);
  }, [needle]);

  if (booting) return <main className="callback-page"><Logo /><div className="loading-orbit" /><h1>Loading the API reference…</h1></main>;
  if (!session) return <Navigate to="/login" replace />;

  const onPage = REF[active] || [];
  const extras = ON_PAGE_EXTRAS[active] || [];

  return <main className="docs-page">
    <header className="docs-header">
      <a className="docs-brand" href="/app" aria-label="Back to the dashboard"><img src="/brand/logo.png" alt="Oxwell" /></a>
      <span className="docs-header__title">Ads API</span>
      <button className="button button--secondary button--small docs-ai-copy" type="button" onClick={copyForAi}>{aiCopied ? <Check /> : <Sparkles />}{aiCopied ? "Copied for AI" : "Copy for AI"}</button>
      <a className="button button--secondary button--small" href="/app/settings/developer">Manage API keys</a>
    </header>

    <div className="docs-layout docs-layout--rail">
      <nav className="docs-toc" aria-label="Sections">
        <label className="docs-search"><Search /><input value={query} placeholder="Search the API…" aria-label="Search the API reference" onChange={(event) => setQuery(event.target.value)} /></label>
        {nav.map(([group, items]) => <div className="docs-nav-group" key={group}>
          <span className="docs-toc__label">{group}</span>
          {items.map(([id, label]) => <a key={id} href={`#${id}`} className={active === id ? "is-active" : ""}>{label}</a>)}
        </div>)}
        {nav.length === 0 && <p className="docs-search__empty">Nothing matches. Try an endpoint path like /events.</p>}
      </nav>

      <article className="docs-body" ref={bodyRef}>
        <div className="docs-hero">
          <span className="docs-eyebrow">API reference</span>
          <h1>Oxwell Ads API</h1>
          <p>Everything the dashboard does, over HTTPS: create campaigns, target audiences, upload creatives, launch ads, track conversions, and read delivery and attribution.</p>
          <div className="docs-hero__facts">
            <div><small>Base URL</small><code>https://ads.oxwell.io/api/v1</code></div>
            <div><small>Auth</small><code>Authorization: Bearer oxk_live_…</code></div>
          </div>
        </div>

        <section id="quickstart">
          <h2>Quickstart: launch a campaign</h2>
          <p className="docs-section-lead">Five requests take you from nothing to a live ad. Create an API key in <a href="/app/settings/developer">Settings → Developer</a> first.</p>
          <p className="docs-note docs-note--warn"><strong>Billing is set up in the dashboard, not the API.</strong> Before a campaign can launch or spend, add the payment method under <a href="/app/settings/billing">Settings → Billing</a> (and connect an identity under <a href="/app/settings/identity">Settings → Identity</a>). Launch and spend requests without active billing fail with <code>409</code>.</p>
          <p className="docs-note"><Sparkles aria-hidden="true" style={{ width: 14, height: 14, verticalAlign: "-2px" }} /> <strong>Working with an AI agent?</strong> Use <em>Copy for AI</em> in the header to copy this whole reference as Markdown, or point your agent at <a href="https://ads.oxwell.io/api/v1/docs.md" target="_blank" rel="noreferrer">https://ads.oxwell.io/api/v1/docs.md</a>: the whole reference as plain Markdown, no authentication needed.</p>
          <Step number={1} title="Verify your key" code={`curl https://ads.oxwell.io/api/v1/me \\
  -H "Authorization: Bearer oxk_live_..."

# { "ad_account_id": "biz_XXXX", "name": "Your ad account", ... }`}>
            <code>/me</code> returns your ad account ID: the identifier you will see on every object.
          </Step>
          <Step number={2} title="Create a draft campaign" code={`curl -X POST https://ads.oxwell.io/api/v1/ad_campaigns \\
  -H "Authorization: Bearer oxk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Summer sale",
    "objective": "sales",
    "platform": "meta"
  }'

# => { "id": "adcamp_XXXX", "status": "draft", ... }`}>
            Campaigns start as drafts, so nothing spends yet. With no budget fields, each ad group carries its own budget.
          </Step>
          <Step number={3} title="Create an ad group with budget and targeting" code={`curl -X POST https://ads.oxwell.io/api/v1/ad_groups \\
  -H "Authorization: Bearer oxk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "ad_campaign_id": "adcamp_XXXX",
    "budget_amount": 50,
    "budget_type": "daily",
    "optimization_goal": "conversions",
    "conversion_event": "purchase",
    "conversion_location": "website",
    "regions": { "include": { "countries": ["US"] } },
    "demographics": { "minimum_age": 21 },
    "placements": "automatic"
  }'`}>
            The ad group owns budget, optimization, and targeting. <code>placements: "automatic"</code> lets the network place the ads.
          </Step>
          <Step number={4} title="Upload a creative" code={`curl -X POST https://ads.oxwell.io/api/v1/files \\
  -H "Authorization: Bearer oxk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{ "filename": "hero.jpg" }'

# => { "id": "file_XXXX", "upload_url": "https://..." }
# PUT the binary to upload_url, then poll GET /files/file_XXXX`}>
            Upload the binary straight to the returned URL, then wait for the file to finish processing.
          </Step>
          <Step number={5} title="Create the ad and launch" code={`curl -X POST https://ads.oxwell.io/api/v1/ads \\
  -H "Authorization: Bearer oxk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "ad_group_id": "adset_XXXX",
    "creatives": [{ "id": "file_XXXX" }],
    "primary_texts": ["Our biggest sale of the year."],
    "headlines": ["Up to 50% off"],
    "call_to_action": "shop_now",
    "url": "https://yourstore.com/sale",
    "social_accounts": [{ "id": "sacc_XXXX" }]
  }'

# Launch: flip the draft campaign live
curl -X PATCH https://ads.oxwell.io/api/v1/ad_campaigns/adcamp_XXXX \\
  -H "Authorization: Bearer oxk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{ "status": "active" }'`}>
            <code>sacc_…</code> is your Facebook Page from <code>GET /social_accounts</code>. Setting the campaign's status to <code>active</code> submits it for review and pushes it live.
          </Step>
        </section>

        <section id="concepts">
          <h2>Core concepts</h2>
          <div className="docs-concepts">
            <div><strong>Scoped keys</strong><p>Each key belongs to one ad account. Every request acts as that account: you never send an account identifier, and one you send is ignored. Your <strong>ad account ID</strong> (<code>biz_…</code>) appears in responses and reports.</p></div>
            <div><strong>Headers</strong><p><code>Authorization: Bearer oxk_live_…</code> on every request. <code>Content-Type: application/json</code> on writes. Optional <code>Idempotency-Key</code> makes any write safe to retry. No version header: Oxwell pins the API version.</p></div>
            <div><strong>Object model</strong><p>A <em>campaign</em> (<code>adcamp_</code>) holds the objective. Its <em>ad groups</em> (<code>adset_</code>) hold budget and targeting. Their <em>ads</em> (<code>ad_</code>) hold creative and copy. Every level supports pause, unpause, and duplicate.</p></div>
            <div><strong>Lifecycle actions</strong><p>Campaigns, ad groups, and ads all expose <code>POST …/{"{id}"}/pause</code>, <code>POST …/{"{id}"}/unpause</code>, and <code>POST …/{"{id}"}/duplicate</code>. They behave identically at each level and are not repeated below.</p></div>
          </div>
        </section>

        <section id="campaigns">
          <h2>Ad campaigns</h2>
          <EndpointIndex rows={REF.campaigns} />
          <Fields label="Create & update fields" rows={[
            ["title", "string, required", "Display name of the campaign."],
            ["objective", "enum, required", "awareness, traffic, engagement, leads, or sales."],
            ["platform", "enum, required", "The ad network. Use meta."],
            ["budget_optimization", "enum", "Who owns the budget: ad_campaign (one shared budget) or ad_group (default; each group budgets itself)."],
            ["budget_amount / budget_type", "number / enum", "The shared budget and whether it is daily or lifetime. Only when budget_optimization is ad_campaign."],
            ["bid_type", "enum", "minimum_cost (default behavior), average_target, or maximum_target; targets use desired_cost_per_result."],
            ["starts_at / ends_at", "ISO 8601", "Delivery window for campaign-budget campaigns."],
            ["special_ad_categories", "enum[]", "housing, employment, financial_products, politics. Adds targeting restrictions."],
            ["status", "enum (update only)", "Set to active to launch a draft. Running campaigns pause and resume via the lifecycle actions."],
          ]} />
          <p className="docs-note">Responses carry full metrics per campaign: <code>spend</code>, <code>impressions</code>, <code>clicks</code>, <code>reach</code>, <code>results</code>, <code>purchases</code>, <code>return_on_ad_spend</code>, plus <code>status</code>, <code>delivery_status</code>, and open <code>issues</code>.</p>
        </section>

        <section id="ad-groups">
          <h2>Ad groups</h2>
          <EndpointIndex rows={REF["ad-groups"]} />
          <Fields label="Create & update fields" rows={[
            ["ad_campaign_id", "string, required", "The campaign to create the group in."],
            ["budget_amount / budget_type", "number / enum", "This group's budget, daily or lifetime. Omit when the campaign owns the budget."],
            ["optimization_goal", "enum", "What delivery maximizes: conversions, link_clicks, landing_page_views, reach, impressions, engagement, conversations, video_views, lead_generation, …"],
            ["conversion_event", "string", "The pixel event optimized for: purchase, lead, add_to_cart, complete_registration, schedule, contact, or any custom pixel event name."],
            ["conversion_location", "enum", "Where the outcome happens: website, profile, messaging, on_ad, instant_forms, and combinations."],
            ["demographics", "object", "{ minimum_age, maximum_age, gender, automatic }. automatic: true enables platform-managed audience expansion."],
            ["regions", "object", "{ include, exclude } with countries (ISO codes), cities, zips, or custom_locations (coordinate + radius)."],
            ["detailed_targeting", "object", "{ interests, behaviors, demographics } category lists; find IDs via targeting_options. Send the complete intended state."],
            ["placements", "\"automatic\" | object[]", "automatic, or per-platform positions: facebook (feed, story, reels…), instagram (stream, reels, explore…), messenger, audience_network, threads."],
            ["audiences", "object", "{ include, exclude } saved audience IDs (adaud_…)."],
            ["devices / languages", "object / string[]", "OS and device-type targeting; languages as ISO 639 codes."],
            ["dynamic_creative", "boolean", "Let the platform mix creatives and copy. Creation only."],
            ["starts_at / ends_at", "ISO 8601", "Delivery window; omit ends_at to run until paused."],
          ]} />
        </section>

        <section id="ads">
          <h2>Ads</h2>
          <EndpointIndex rows={REF.ads} />
          <Fields label="Create & update fields" rows={[
            ["ad_group_id", "string", "The group to create the ad in; or pass ad_group, an inline ad group object, to create both at once."],
            ["creatives", "object[]", "Uploaded files: [{ id: \"file_…\", format?, crop? }]. Two or more entries without formats become a carousel (2-10, in order)."],
            ["primary_texts / headlines / descriptions", "string[]", "Copy variants shown on the ad."],
            ["call_to_action", "enum", "shop_now, learn_more, sign_up, subscribe, contact_us, download, get_offer, book_now, …"],
            ["url", "string", "Destination URL. Its query string is split into url_parameters automatically."],
            ["url_parameters", "object", "Extra query parameters; attribution keys (utm_source, wacid, …) are reserved."],
            ["social_accounts", "object[]", "The identities the ad runs under: [{ id: \"sacc_…\" }] from GET /social_accounts."],
            ["existing_post_id / post_source", "string / enum", "Promote an existing Facebook or Instagram post instead of creatives."],
            ["lead_form / lead_form_id", "object / string", "Instant lead form, when the group's conversion_location is an instant-forms destination."],
          ]} />
        </section>

        <section id="files">
          <h2>Files</h2>
          <EndpointIndex rows={REF.files} />
          <p className="docs-note">Upload the binary with a <code>PUT</code> to the returned <code>upload_url</code>, sending every header from the response&rsquo;s <code>upload_headers</code> verbatim. Use <code>multipart: true</code> above ~100MB (5MB parts, required past 5GB).</p>
        </section>

        <section id="social">
          <h2>Social accounts</h2>
          <EndpointIndex rows={REF.social} />
          <p className="docs-note">Connect or create identities in the dashboard under Settings → Identity; pass their entries in an ad's <code>social_accounts</code>.</p>
        </section>

        <section id="pixel">
          <h2>Pixel & websites</h2>
          <p className="docs-section-lead">The pixel measures page views on your website, links visitors to their purchases, and attributes conversions back to your ads with first-party data. Install it once on every page of your funnel.</p>
          <h3>Install the snippet</h3>
          <p>Paste this inside the <code>&lt;head&gt;</code> of every page: landing pages, advertorials, checkouts, thank-you pages. The dashboard's <a href="/app/tracking">Pixel &amp; websites</a> tab shows this snippet with your ad account ID already filled in; here it carries the placeholder <code>biz_XXXXXXXXXXXX</code>.</p>
          <CodeBlock lang="html" code={`<script>
${pixelInlineScript(DOCS_ACCOUNT_ID)}
</script>`} />
          <h3>Install on a site builder</h3>
          <p>Most funnel builders have a slot for header or tracking code. Paste the snippet there, not into a page's text or content block, so it loads on every page. For these platforms the standard snippet works as-is; only the slot's location differs.</p>
          <Fields label="Where the snippet goes" rows={[
            ["GoHighLevel", "header code", "Sites → Funnels (or Websites) → open it → Settings → Tracking Code → Header Tracking. Setting it at the funnel level covers every page inside it."],
            ["ClickFunnels", "header code", "Funnel Settings → Custom Code → Head Code. Use the Sites app's global Header Code to cover every funnel at once."],
            ["Kajabi", "header code", "Settings → Site Details → Page Scripts → Header Page Scripts."],
            ["WordPress", "plugin", "WordPress does not expose the <head> by default. Install a header-script plugin (WPCode or Insert Headers and Footers) and paste into its Header field. This also covers Elementor, Divi, and other builders."],
            ["WooCommerce", "plugin", "Same as WordPress: a header-script plugin's Header field. Covers the shop, product, and cart pages."],
          ]} />
          <p className="docs-note">On a builder with no header slot at all, add the same snippet through Google Tag Manager as a Custom HTML tag that fires on all pages. Shopify, WebinarJam, Calendly, and iClosed need more than the standard snippet: their full walkthroughs follow.</p>

          <h3>Shopify</h3>
          <p>Shopify's checkout runs in a sandboxed environment that ignores scripts pasted into <code>theme.liquid</code>, so a normal install never sees it. Shopify's own <strong>Custom Pixel</strong> feature is the way around that: it runs in every part of the store, checkout included, and reports standard events like <code>checkout_completed</code> directly. The script below listens for those events and forwards each one, with the order's value, the buyer's contact and address fields, and the ad-click IDs from the landing URL.</p>
          <p>In Shopify go to <strong>Settings → Customer events → Add custom pixel</strong>, name it, and paste this into the pixel's code editor (the in-app Shopify guide has it with your account ID filled in). Then click <strong>Save</strong> and <strong>Connect</strong>: Shopify won't send any events to an unconnected pixel.</p>
          <CodeBlock lang="javascript" code={SHOPIFY_PIXEL_SCRIPT} />
          <p className="docs-note"><strong>Keep the purchase mapping.</strong> A Shopify order is processed entirely inside Shopify, and this script's <code>checkout_completed</code> (sent as <code>purchase</code>, with the order value) is the only thing that records it, so the ads that drove the sale get credit.</p>
          <p>To confirm: visit a live storefront page, check the dashboard's <a href="/app/tracking">Pixel & websites</a> tab for your domain, then place a test order and confirm a purchase lands too.</p>

          <h3>WebinarJam</h3>
          <p>In WebinarJam open your webinar, go to <strong>Integrations → Integrate a 3rd Party Tracking System → Embed Your Custom Tracking Code</strong>. WebinarJam gives you a separate tracking field per funnel step; each one gets a snippet:</p>
          <Fields label="What goes in each field" rows={[
            ["Registration Form Tracking", "plain snippet", "The form is a popup with its own context, separate from the page around it. The visitor's email, name, and phone fields live here, so this placement is what lets the pixel's automatic field capture see them."],
            ["Registration Page Tracking", "snippet + lead listener", "The standard snippet plus the listener below, which fires the lead event the instant registration completes, before the redirect to the thank-you page happens."],
            ["Post-Registration Thank You Page", "plain snippet", "A backup page-view record."],
            ["Live room / Replay page", "plain snippet (optional)", "Add if you want visits to those steps tracked."],
          ]} />
          <p>The Registration Page field gets the standard snippet followed by this listener:</p>
          <CodeBlock lang="javascript" code={`window.addEventListener("message", function (e) {
  if (e.origin !== "https://event.webinarjam.com") return;

  let data;
  try {
    data = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
  } catch {
    return;
  }

  if (data.func === "redirect" && typeof data.url === "string" && data.url.indexOf("/thank-you") !== -1) {
    oxwell.track("lead");
  }
});`} />
          <p className="docs-note">Running registration on your own site instead of WebinarJam's hosted page? Install the snippet on that page directly; WebinarJam's tracking fields only cover its own hosted pages.</p>

          <h3>Calendly & iClosed</h3>
          <p>Neither booking tool can tell your website when someone books, so bookings can't be tracked the normal way. The fix is the same for both: send bookers to a thank-you page on your own site after they book, and record the booking there. The booker's name and email ride along in the redirect URL and are picked up automatically.</p>
          <Fields label="The four steps" rows={[
            ["1. Booking page", "snippet", "Install the standard snippet on the page where people actually book (your landing page, or wherever the calendar is embedded). Without it you see only the final booking, no page views or ad clicks."],
            ["2. Thank-you page", "snippet", "Build a simple confirmation page on your own site and install the standard snippet there too. Every page in the funnel needs its own copy."],
            ["3. Redirect", "platform setting", "Calendly: event type → Edit → More options → Confirmation page → Redirect to an external site, and turn ON “Pass event details to your redirected page” (that switch is what carries the booker's name and email). iClosed: event settings → Confirmation Page → your thank-you URL."],
            ["4. Record the booking", "schedule snippet", "Right under the pixel snippet on the thank-you page, add the tracker below."],
          ]} />
          <CodeBlock lang="html" code={`<script>
var params = new URLSearchParams(window.location.search);
oxwell.track("schedule", {
  email: params.get("invitee_email") || undefined,
  name: params.get("invitee_full_name") || undefined,
});
</script>`} />
          <p className="docs-note">Embedding Calendly's scheduler on your own page (Share → Add to Website) instead of linking out? You can skip the redirect: keep the snippet on that page and listen for <code>calendly.event_scheduled</code> via <code>window.addEventListener("message", ...)</code>, firing <code>oxwell.track("schedule")</code> when it arrives. That confirms the booking but carries no name or email (Calendly keeps those out of the client-side event), so pass details you collected earlier in the funnel yourself, or use the redirect method.</p>
          <h3>Standard events</h3>
          <p>The snippet records page views by itself. Everything else is an <code>oxwell.track</code> call from your pages: leads, bookings, applications, and sales on your checkout. Every event optionally takes <code>value</code> (number) and <code>currency</code> (ISO 4217 code); <code>purchase</code> requires a positive <code>value</code>.</p>
          <Fields label="Standard event names" rows={[
            ["lead", "event", "A visitor submits contact info or an opt-in form."],
            ["schedule", "event", "A visitor books a call or appointment."],
            ["submit_application", "event", "A visitor submits an application."],
            ["contact", "event", "A visitor starts a conversation or contact request."],
            ["complete_registration", "event", "A signup or registration finishes."],
            ["view_content", "event", "A visitor views a key page or piece of content."],
            ["add_to_cart", "event", "A visitor adds an item to a cart."],
            ["purchase", "event", "A sale completes on your checkout. Requires a positive value."],
          ]} />
          <h3>Custom events</h3>
          <CodeBlock lang="javascript" code={`oxwell.track("lead");                                      // a standard event
oxwell.track("schedule", { value: 50, currency: "USD" });  // optionally with a value
oxwell.track("watched_vsl");                               // or your own event name
oxwell.track("quiz_completed", { value: 25, currency: "USD" });`} />
          <p className="docs-note">Custom names show up in your reporting and in <code>conversion_event</code> targeting on ad groups. Keep names short and stable: names under about 34 characters also forward to Meta as custom conversions.</p>
          <h3>Where to fire events</h3>
          <p>Where the call goes depends on what happens after the action.</p>
          <p><strong>The action redirects</strong> (a form that sends the visitor to a thank-you page): fire the event on the page they land on. The call goes in that page's HTML or a page builder's code field, so wrap it in <code>&lt;script&gt;</code> tags. The pixel snippet must be installed on that page too.</p>
          <CodeBlock lang="html" code={`<script>
  oxwell.track("lead");
</script>`} />
          <p><strong>The action stays on the same page</strong> (a form that shows an inline success message): fire the event in the action's success handler. You're already inside JavaScript, so no <code>&lt;script&gt;</code> tags.</p>
          <CodeBlock lang="javascript" code={`form.addEventListener("submit", async (e) => {
  e.preventDefault();
  await submitForm();
  oxwell.track("lead");
});`} />
          <h3>Sales on your checkout</h3>
          <p>When a sale completes on your checkout, fire a <code>purchase</code> event. <code>value</code> is required: a purchase with no positive value is rejected, because ad platforms can't optimize toward a sale with no amount. <code>currency</code> is optional and defaults to USD.</p>
          <CodeBlock lang="javascript" code={`oxwell.track("purchase", {
  value: 149.99,
  currency: "USD",
  event_id: "order_8412",
  email: "visitor@example.com",
});`} />
          <p className="docs-note">These are forwarded to the ad platforms as their standard Purchase event, so a purchase-optimized campaign learns from them, and reporting attributes each sale to the ad that drove it. A sale that never reaches the pixel or the <a href="#capi">server-side API</a> is invisible to attribution. Send each sale once: same <code>event_id</code> from browser and server.</p>
          <h3>Event IDs</h3>
          <p>Pass an <code>event_id</code> with any event that could arrive more than once. Each <code>event_name</code> and <code>event_id</code> pair counts once, so a retry, a page refresh, or the same conversion sent from the browser and from your server collapses into a single event.</p>
          <CodeBlock lang="javascript" code={`oxwell.track("lead", { event_id: "lead_8f21c0" }); // one visitor's form submission
oxwell.track("lead", { event_id: "lead_2b94de" }); // a different visitor's`} />
          <p>The ID identifies one single event, not the type of event. Every lead needs its own: send <code>"lead"</code> as the ID for every lead and hundreds of leads show up as one. Use a value your own system already has for that action (a lead ID, order number, or form submission ID) and reuse it only when sending that same action again. A value made up at fire time is new every time, so it won't stop duplicates.</p>
          <h3>Customer fields</h3>
          <p>Send as much customer information as you have on every event. It matches events to customers and improves conversion tracking. All fields are optional; send only what you have, as plain text. A hashed email is dropped because it isn't a valid address, and a hashed phone number is stored as meaningless digits that match nobody.</p>
          <CodeBlock lang="javascript" code={`oxwell.track("lead", {
  email: "visitor@example.com",
  first_name: "Jane",
  last_name: "Doe",
  name: "Jane Doe",
  phone: "+15551234567",
  external_id: "customer_123",
  city: "New York",
  state: "NY",
  postal_code: "10001",
  country: "US",
});`} />
          <Fields label="Customer fields" rows={[
            ["email", "string", "Visitor's email address."],
            ["first_name / last_name / name", "string", "Visitor's first name, last name, and full display name."],
            ["phone", "string", "Visitor's phone number."],
            ["external_id", "string", "Your own user or customer ID."],
            ["city / state / postal_code / country", "string", "Visitor's city, state or region, postal or ZIP code, and country."],
          ]} />
          <h3>Verify the install</h3>
          <EndpointIndex rows={REF.pixel} />
          <p>The dashboard's <a href="/app/tracking">Pixel & websites</a> tab shows live status and every domain reporting events. Website analytics (visits by hostname, page, device, and source) are available through the <a href="#reporting">traffic metrics</a>.</p>
        </section>

        <section id="capi">
          <h2>Server-side events (CAPI)</h2>
          <p className="docs-section-lead">The conversions API is the server-side twin of the pixel: send purchases, leads, and any custom event from your backend so conversions that happen off the page (or after ad blockers) still attribute to your ads and improve delivery optimization.</p>
          <EndpointIndex rows={REF.capi} />
          <h3>Send an event</h3>
          <CodeBlock code={`curl -X POST https://ads.oxwell.io/api/v1/events \\
  -H "Authorization: Bearer oxk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "event_name": "lead",
    "event_time": "2026-08-29T14:03:00Z",
    "event_id": "lead_84921",
    "value": 25,
    "currency": "usd",
    "url": "https://yourstore.com/thanks",
    "action_source": "website",
    "user": {
      "email": "jane@example.com",
      "phone": "+15551234567",
      "external_id": "cust_84921",
      "first_name": "Jane",
      "country": "US"
    },
    "context": {
      "fbp": "fb.1.1725900000.1234567890",
      "fbc": "fb.1.1725900000.AbCdEfGh",
      "ip_address": "203.0.113.7",
      "user_agent": "Mozilla/5.0 ..."
    }
  }'`} />
          <Fields label="Event fields" rows={[
            ["event_name", "string, required", "A standard pixel event: lead, schedule, submit_application, contact, complete_registration, view_content, add_to_cart. Any other name (or custom with custom_name) records as a custom event under that name. A purchase (a sale on your checkout) must include a positive value or it is rejected; it counts toward purchase-optimized ad groups and is forwarded to the ad platforms as a standard Purchase. Send each sale once: reuse the pixel's event_id if the browser also reports it."],
            ["custom_name", "string", "Your event name when event_name is custom. Up to 35 characters."],
            ["event_time", "ISO 8601", "When the event occurred. Defaults to now."],
            ["event_id", "string", "Your identifier for deduplication: send the same event_id from the browser and the server and the event counts once."],
            ["value / currency", "number / enum", "Monetary value and ISO 4217 currency (usd, eur, gbp, …)."],
            ["url", "string", "The page where the event occurred."],
            ["action_source", "enum", "Where it originated: website, app, email, phone_call, chat, physical_store, system_generated, business_messaging, other."],
          ]} />
          <Fields label="user: match keys" rows={[
            ["email / phone", "string", "The strongest match keys. Send raw values; hashing is handled downstream."],
            ["external_id", "string", "Your own customer ID: keeps matches stable across email changes."],
            ["first_name / last_name / name", "string", "Name fields to strengthen matching."],
            ["city / state / postal_code / country", "string", "Address fields to strengthen matching."],
            ["birthdate / gender", "string / enum", "YYYY-MM-DD; male or female."],
            ["anonymous_id / linked_anonymous_id", "string", "The pixel's anonymous visitor IDs, to join server events with browser sessions (also across iframes)."],
            ["user_id / member_id / membership_id / username", "string", "Platform identifiers when you know them."],
          ]} />
          <Fields label="context: attribution signals" rows={[
            ["fbp / fbc / fbclid", "string", "Facebook browser pixel cookie, click cookie, and click ID: forward these for Meta attribution."],
            ["gclid / gbraid / wbraid", "string", "Google click IDs."],
            ["ttclid / ttp", "string", "TikTok click ID and pixel ID."],
            ["msclkid / twclid / li_fat_id / sccid / rdt_cid", "string", "Microsoft, X, LinkedIn, Snapchat, and Reddit click IDs."],
            ["ip_address / user_agent", "string", "The visitor's IP and browser user agent, from your server logs."],
            ["utm_source / utm_medium / utm_campaign / utm_content / utm_term / utm_id", "string", "UTM parameters from the landing session."],
            ["ad_campaign_id / ad_set_id / ad_id", "string", "Attribute directly to a campaign, ad group, or ad when you know it."],
            ["ga / language / timezone / screen_resolution / fingerprint", "string", "Additional analytics and device signals."],
          ]} />
          <p className="docs-note"><strong>Deduplication:</strong> when the pixel and your server both report the same conversion, give both the same <code>event_id</code>. Reading back: <code>GET /events?identifier=jane@example.com</code> shows a person's full journey; filter with <code>event</code> and <code>source</code> (e.g. <code>ext:meta:*</code> for ad-driven events).</p>
          <h3 id="instant-form-leads">Instant-form leads</h3>
          <p>Leads collected by Meta instant forms on your ads are recorded as <code>lead</code> events on the ad account, with the person's contact details and every form answer attached. They are not a separate resource: read them from <code>GET /events</code>.</p>
          <CodeBlock lang="bash" code={`curl "https://ads.oxwell.io/api/v1/events?event=pixel.lead&from=2026-09-01T00:00:00Z&to=2026-09-01T12:00:00Z" \\
  -H "Authorization: Bearer oxk_live_..."`} />
          <Fields label="Each lead event" rows={[
            ["event_name", "string", "Always pixel.lead for a form lead."],
            ["event_id", "string", "Meta's id for the submission. Stable across polls, so use it to skip leads you already processed."],
            ["event_time", "ISO 8601", "When the person submitted the form."],
            ["user", "object", "email, phone, first_name, last_name, name, city, country, as the person typed them into the form."],
            ["questions", "array", "Every question on the form with its answer: { question, answer, key, type, options }. Custom questions appear here too."],
            ["person_id", "string", "The ad network's identity for the person, for GET /events?identifier=... to see their full journey."],
          ]} />
          <p className="docs-note"><strong>Timing:</strong> instant-form leads sync in from Meta every 15 minutes, so a lead appears here within about 15 minutes of the submission. To forward leads to a CRM, poll this endpoint every few minutes with <code>from</code> set to your previous check and push each new <code>event_id</code>. Leads also count in the <code>leads</code> metric on campaigns, ad groups, and ads.</p>
        </section>

        <section id="reporting">
          <h2>Reporting</h2>
          <EndpointIndex rows={REF.reporting} />
          <Fields label="Stats query parameters" rows={[
            ["from / to", "date | ISO 8601, required", "The range. Dates expand to full days; timestamps are used exactly."],
            ["interval", "enum", "minute, hour, day (default), week, month, year."],
            ["breakdown_by", "string", "Split each point by a property. breakdown_by=metric on ad_delivery returns spend, impressions, and clicks together. Traffic metrics break down by source, hostname, page, and device_type."],
            ["hostname / page / device_type / source", "string", "Filters on the traffic metrics: one website, one page, one device type, or one visit source (whop_ads, direct, or a utm_source)."],
            ["time_zone", "string", "IANA zone for bucketing, e.g. America/New_York. Defaults to UTC."],
            ["convert_to", "string", "Display currency for money metrics. Defaults to usd."],
          ]} />
          <CodeBlock code={`# Ad spend by day
curl "https://ads.oxwell.io/api/v1/stats/ad_spend?from=2026-08-01&to=2026-08-28" \\
  -H "Authorization: Bearer oxk_live_..."

# Website visits split by page (the traffic metric is page_visits; see GET /stats for all keys)
curl "https://ads.oxwell.io/api/v1/stats/page_visits?from=2026-08-01&to=2026-08-28&breakdown_by=page" \\
  -H "Authorization: Bearer oxk_live_..."`} />
        </section>

        <section id="conventions">
          <h2>Conventions & errors</h2>
          <div className="docs-concepts">
            <div><strong>Pagination</strong><p>Lists return <code>{`{ data, page_info }`}</code>. Page with <code>first</code> and <code>after</code> (from <code>page_info.end_cursor</code>); some lists also take <code>before</code> and <code>direction</code>.</p></div>
            <div><strong>Errors</strong><p>Failures return <code>{`{ error: { message, type, code? } }`}</code>; authentication failures from Oxwell return <code>{`{ message }`}</code>.</p></div>
          </div>
          <EndpointIndex rows={REF.conventions} />
          <footer className="docs-footer">Questions? <a href="mailto:hello@oxwell.io">hello@oxwell.io</a></footer>
        </section>
      </article>

      <aside className="docs-onpage" aria-label="On this page">
        <span className="docs-toc__label">On this page</span>
        {extras.map((label) => <span className="docs-onpage__item" key={label}>{label}</span>)}
        {onPage.map(([method, path]) => <a key={`${method}${path}`} href={`#${slugify(method, path)}`}>
          <span className={`docs-method docs-method--${method.toLowerCase()}`}>{method}</span>
          <code>{path}</code>
        </a>)}
      </aside>
    </div>
  </main>;
}
